"use client"

import { useCallback, useRef } from "react"
import { useAuth } from "@/hooks/use-auth"
import { clearAllResponseCaches } from "@/components/shopify/economics/_response-cache"

export interface ProductCogsUpsert {
  product_id: number
  /** Omit or null for a product-level cost. */
  variant_id?: number | null
  cogs_per_unit: number
  title: string
  sku?: string | null
  variant_title?: string | null
  notes?: string | null
}

/**
 * Writes manual COGS overrides of one store. Costs are resolved on the server, so every
 * successful write clears the cached economics responses.
 */
export function useProductCogs({ integrationId }: { integrationId: string }) {
  const { fetchWithAuth } = useAuth()
  const fetchRef = useRef(fetchWithAuth)
  fetchRef.current = fetchWithAuth
  const integrationIdRef = useRef(integrationId)
  integrationIdRef.current = integrationId

  const updateCogs = useCallback(async (items: ProductCogsUpsert[]) => {
    const body = items.length === 1 ? items[0] : { cogs: items }
    const params = new URLSearchParams({ integration_id: integrationIdRef.current })
    const res = await fetchRef.current(`/api/integrations/shopify/product-cogs?${params}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error ?? "Failed to update COGS")
    }
    clearAllResponseCaches()
  }, [])

  const deleteCogs = useCallback(async (productId: number, variantId?: number | null) => {
    const params = new URLSearchParams({
      integration_id: integrationIdRef.current,
      product_id: String(productId),
    })
    if (variantId != null) params.set("variant_id", String(variantId))
    const res = await fetchRef.current(`/api/integrations/shopify/product-cogs?${params}`, {
      method: "DELETE",
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.error ?? "Failed to delete COGS")
    }
    clearAllResponseCaches()
  }, [])

  return { updateCogs, deleteCogs }
}
